import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Badge, Button, Form, Overlay } from "react-bootstrap";
import { Filter, X } from "react-feather";

const AccessProfileFilter = (props) => {
  const {
    column: { filterValue, setFilter, filterOptions },
  } = props;

  const filterPrefix = "$PROFILE=";
  const filterSeperator = "|";
  const filterBlankLabel = "[BLANKS]";

  // Control if the selection panel is visible
  const [show, setShow] = useState(false);
  const target = useRef(null);

  // Remember the options as they can come from a promise
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  useEffect(() => {
    if (!filterOptions) {
      console.log("❌ No filter options");
      return null;
    }

    let isMounted = true; // prevent updates after unmount

    const loadOptions = async () => {
      setLoading(true);
      try {
        // Handle array directly
        if (Array.isArray(filterOptions)) {
          if (isMounted) setOptions(filterOptions);
        }
        // Handle promise
        else if (filterOptions instanceof Promise) {
          const result = await filterOptions;
          if (isMounted) setOptions(result);
        }
        // Handle async function
        else if (typeof filterOptions === "function") {
          const result = await filterOptions();
          if (isMounted) setOptions(result);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadOptions();

    return () => {
      isMounted = false;
    };
  }, [filterOptions]);

  // parse the filterValue into the selected options (a string array of labels)
  // and booleans for blanks and all.
  const parseFilterValue = useCallback(() => {
    if (
      filterValue &&
      typeof filterValue === "string" &&
      filterValue.startsWith(filterPrefix)
    ) {
      var selectedOptions = filterValue
        .substring(filterPrefix.length)
        .split(filterSeperator);
      var selectedBlanks = selectedOptions.some((v) => v === filterBlankLabel);
      var selectedAll =
        options.every((option) =>
          selectedOptions.some(
            (selectedOption) => selectedOption === option.value
          )
        ) && selectedBlanks;
      return {
        selectedOptions: selectedOptions,
        selectedBlanks: selectedBlanks,
        selectedAll: selectedAll,
      };
    }
    return { selectedOptions: [], selectedBlanks: false, selectedAll: false };
  }, [options, filterValue]);

  var { selectedOptions, selectedBlanks, selectedAll } = useMemo(
    () => parseFilterValue(),
    [parseFilterValue]
  );

  var selectedCount = selectedOptions.length;

  const updateFilter = useCallback(
    ({
      newSelectedOptions = undefined,
      newSelectedBlanks = undefined,
      newSelectedAll = undefined,
    }) => {
      if (
        typeof newSelectedOptions === "undefined" &&
        typeof newSelectedBlanks === "undefined" &&
        typeof newSelectedAll === "undefined"
      ) {
        setFilter("");
        return;
      }

      var newFilterOptions = [...selectedOptions];

      if (typeof newSelectedAll === "boolean") {
        if (newSelectedAll) {
          newFilterOptions = []
            .concat(options.map((_) => _.value))
            .concat(filterBlankLabel);
        } else {
          setFilter("");
          return;
        }
      } else if (
        typeof newSelectedOptions === "object" &&
        Array.isArray(newSelectedOptions)
      ) {
        newFilterOptions = newSelectedOptions;
      }

      if (typeof newSelectedBlanks === "boolean" && newSelectedBlanks) {
        newFilterOptions = newFilterOptions.concat([filterBlankLabel]);
      } else if (typeof newSelectedBlanks === "boolean" && !newSelectedBlanks) {
        // Remove blanks
        newFilterOptions = newFilterOptions.filter(
          (_) => _ !== filterBlankLabel
        );
      }

      // Check if we have anything selected
      var filter =
        newFilterOptions.length > 0
          ? filterPrefix + newFilterOptions.join(filterSeperator)
          : "";
      setFilter(filter);
    },
    [options, selectedOptions, selectedBlanks]
  );

  const isSelected = (value) => {
    return selectedOptions.some((_) => _ === value);
  };

  const handleSelectAll = (e) => {
    updateFilter({ newSelectedAll: e.target.checked });
  };

  const handleSelectBlanks = (e) => {
    updateFilter({ newSelectedBlanks: e.target.checked });
  };

  const handleCheckChange = (e) => {
    const label = e.target.name;
    const value = e.target.id;
    const checked = e.target.checked;

    var newSelectedOptions = selectedOptions ?? [];
    if (checked) {
      // Add label to selected array
      newSelectedOptions = [...newSelectedOptions, value];
    } else {
      // Remove label from selected array
      newSelectedOptions = newSelectedOptions.filter((_) => _ !== value);
    }
    updateFilter({ newSelectedOptions: newSelectedOptions });
  };

  return (
    <React.Fragment>
      {!loading && (
        <React.Fragment>
          <div className="d-flex flex-nowrap">
            <Button variant="light" ref={target} onClick={() => setShow(!show)}>
              <Filter size={"1.2em"} className="m-0 p-0 me-1" />
              <Badge pill bg="dark" text="light">
                {selectedCount}
              </Badge>
            </Button>
            <Button
              variant=""
              className="p-0 m-0"
              onClick={() => {
                updateFilter({});
              }}
            >
              <X />
            </Button>
          </div>

          <Overlay
            target={target.current}
            show={show}
            placement="bottom-start"
            rootClose={true}
            rootCloseEvent={"click"}
            onHide={() => {
              setShow(false);
            }}
          >
            {({
              placement: _placement,
              arrowProps: _arrowProps,
              show: _show,
              popper: _popper,
              hasDoneInitialMeasure: _hasDoneInitialMeasure,
              ...props
            }) => (
              <div
                {...props}
                style={{
                  backgroundColor: "var(--bs-body-bg)",
                  border: "1px solid black",
                  borderRadius: 3,
                  maxHeight: "512px",
                  ...props.style,
                }}
                className="p-2 overflow-auto"
              >
                <Form.Check
                  type={"checkbox"}
                  id={"select-all"}
                  label={
                    <>
                      <i>Select all</i>
                    </>
                  }
                  checked={selectedAll}
                  name={"select-all"}
                  onChange={(e) => {
                    handleSelectAll(e);
                  }}
                  style={{ color: "var(--bs-body-color)" }}
                />
                {options &&
                  options.map((type) => (
                    <div key={`default-${type.value}`} className="fg-secondary">
                      <Form.Check
                        type={"checkbox"}
                        id={type.value}
                        label={type.label}
                        checked={isSelected(type.value)}
                        name={type.label}
                        onChange={(e) => {
                          handleCheckChange(e);
                        }}
                        style={{ color: "var(--bs-body-color)" }}
                      />
                    </div>
                  ))}
                <Form.Check
                  type={"checkbox"}
                  id={"(Blanks)"}
                  label={
                    <>
                      <i>(Blanks)</i>
                    </>
                  }
                  checked={selectedBlanks}
                  name={"(Blanks)"}
                  onChange={(e) => {
                    handleSelectBlanks(e);
                  }}
                  style={{ color: "var(--bs-body-color)" }}
                />
              </div>
            )}
          </Overlay>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default AccessProfileFilter;
